import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AlternateEmailRounded from '@mui/icons-material/AlternateEmailRounded';
import ForwardToInboxRounded from '@mui/icons-material/ForwardToInboxRounded';
import LinkOffRounded from '@mui/icons-material/LinkOffRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import VisibilityRounded from '@mui/icons-material/VisibilityRounded';
import { Link as RouterLink, useSearchParams } from 'react-router';
import { useNotify } from '@/components/feedback/notificationsContext';
import { fmt } from '@/features/notas/calculo';
import { Destaque } from '@/features/notas/componentes';
import {
  adicionaEmailAdmin,
  buscaAlunosAdmin,
  buscaResumoAdmin,
  desfazVinculoAdmin,
  emailTesteAdmin,
  type AlunoAdmin,
} from '@/features/notas/service';
import { dataBr, nomeProprio } from '@/features/notas/texto';
import { useAsync } from '@/hooks/useAsync';
import { buscaSemestresAdmin } from '@/features/notas/service';
import { CabecalhoOrdenavel as Cabecalho } from '@/components/CabecalhoOrdenavel';
import { PageHeader } from './PageHeader';
import { AcessosAba } from './admin/AcessosAba';
import { EmailsAba } from './admin/EmailsAba';
import { PublicacaoAba } from './admin/PublicacaoAba';
import { ContatosAba } from './admin/ContatosAba';
import { SeletorSemestre } from '@/features/notas/SeletorSemestre';

type Campo = 'nome' | 'p1' | 'vinculo';

const semAcento = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

export function AdminPage() {
  const [params, setParams] = useSearchParams();
  const semestres = useAsync((s) => buscaSemestresAdmin(s), []);
  const semestre = params.get('semestre') ?? semestres.data?.[0]?.semestre ?? '20262';
  const resumo = useAsync((s) => buscaResumoAdmin(semestre, s), [semestre]);
  const alunos = useAsync((s) => buscaAlunosAdmin(semestre, s), [semestre]);
  const notify = useNotify();
  const [busca, setBusca] = useState('');
  const [turma, setTurma] = useState<string>('todas');
  const [desfazer, setDesfazer] = useState<AlunoAdmin | null>(null);
  const [emailPara, setEmailPara] = useState<AlunoAdmin | null>(null);
  const [novoEmail, setNovoEmail] = useState('');
  const [testando, setTestando] = useState(false);
  type Aba = 'alunos' | 'publicacao' | 'contatos' | 'acessos' | 'emails';
  const aba = (
    ['alunos', 'publicacao', 'contatos', 'acessos', 'emails'].includes(params.get('aba') ?? '')
      ? params.get('aba')
      : 'alunos'
  ) as Aba;
  const muda = (mudancas: Record<string, string>) =>
    setParams((p) => ({ ...Object.fromEntries(p), ...mudancas }));

  const testaEmail = async () => {
    setTestando(true);
    try {
      const r = await emailTesteAdmin();
      notify(`E-mail de teste enviado para ${r.para}.`);
    } catch {
      notify('O envio falhou. Veja os logs do Cloud Run (fatec-notas-api).');
    } finally {
      setTestando(false);
    }
  };

  const [ordem, setOrdem] = useState<{ campo: Campo; dir: 'asc' | 'desc' }>({
    campo: 'nome',
    dir: 'asc',
  });
  const ordena = (campo: Campo) =>
    setOrdem((o) =>
      o.campo === campo
        ? { campo, dir: o.dir === 'asc' ? 'desc' : 'asc' }
        : { campo, dir: campo === 'nome' ? 'asc' : 'desc' },
    );

  const filtrados = useMemo(() => {
    const q = semAcento(busca.trim());
    const lista = (alunos.data ?? []).filter(
      (a) =>
        (turma === 'todas' || a.matriculas.some((m) => m.disciplina === turma)) &&
        (!q || semAcento(a.nome).includes(q) || a.ra.includes(q)),
    );
    // P1 da turma escolhida; em "Todas", a maior P1 do aluno
    const p1 = (a: AlunoAdmin) => {
      const ms = a.matriculas.filter((m) => turma === 'todas' || m.disciplina === turma);
      const vs = ms.map((m) => m.notas.P1).filter((v): v is number => typeof v === 'number');
      return vs.length ? Math.max(...vs) : -1;
    };
    // vínculo: confirmado (mais recente primeiro) > link enviado > ainda não entrou
    const vinculo = (a: AlunoAdmin) =>
      a.vinculado_em
        ? 2e13 + Date.parse(a.vinculado_em)
        : a.pendente_desde
          ? 1e13 + Date.parse(a.pendente_desde)
          : 0;
    const chave = { nome: (a: AlunoAdmin) => semAcento(a.nome), p1, vinculo }[ordem.campo];
    const sinal = ordem.dir === 'asc' ? 1 : -1;
    return [...lista].sort((x, y) => {
      const a = chave(x);
      const b = chave(y);
      const c =
        typeof a === 'string' ? a.localeCompare(b as string) : (a as number) - (b as number);
      return c * sinal || semAcento(x.nome).localeCompare(semAcento(y.nome));
    });
  }, [alunos.data, busca, turma, ordem]);

  const r = resumo.data;
  return (
    <>
      <PageHeader
        title="Painel do professor"
        actions={
          <>
            <SeletorSemestre
              semestres={(semestres.data ?? []).map((x) => x.semestre)}
              valor={semestre}
              onChange={(x) => muda({ semestre: x })}
            />
            <Button
              variant="outlined"
              startIcon={<ForwardToInboxRounded />}
              disabled={testando}
              onClick={() => void testaEmail()}
            >
              Testar o e-mail
            </Button>
          </>
        }
        subtitle={
          r?.importacao
            ? `Notas importadas em ${dataBr(r.importacao.importado_em, true)} (planilhas de ${dataBr(r.importacao.gerado_em, true)})`
            : undefined
        }
      />
      <Tabs
        value={aba}
        onChange={(_, v: string) => muda({ aba: v })}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="alunos" label="Alunos" />
        <Tab value="publicacao" label="Publicação" />
        <Tab value="contatos" label="Contatos" />
        <Tab value="acessos" label="Acessos" />
        <Tab value="emails" label="E-mails" />
      </Tabs>
      {aba === 'publicacao' && <PublicacaoAba semestre={semestre} />}
      {aba === 'contatos' && <ContatosAba />}
      {aba === 'acessos' && <AcessosAba />}
      {aba === 'emails' && <EmailsAba />}
      {aba === 'alunos' && (
        <>
          {(resumo.error || alunos.error) && (
            <Alert severity="error">Não consegui carregar os dados.</Alert>
          )}

          {r && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Destaque
                  rotulo="Contas que entraram"
                  valor={r.contas.contas}
                  rodape={`${r.contas.ativas_7d} nos últimos 7 dias`}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Destaque
                  rotulo="RAs confirmados"
                  valor={r.contas.vinculadas}
                  rodape={`de ${alunos.data?.length ?? '…'} alunos`}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Destaque
                  rotulo="Turmas"
                  valor={r.turmas.length}
                  rodape={r.turmas.map((t) => t.codigo).join(' · ')}
                />
              </Grid>
              {r.turmas.map((t) => (
                <Grid key={t.codigo} size={{ xs: 12, sm: 6, md: 4, lg: 'grow' }}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="overline" color="text.secondary">
                        {t.codigo} · {t.turma} · {t.periodo}
                      </Typography>
                      <Typography variant="h5" component="p">
                        P1 média {fmt(t.media_p1)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t.alunos} alunos · {t.p1_acima_6} com P1 ≥ 6 · {t.sem_entrega} sem entrega
                        · {t.vinculados} confirmados
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            sx={{ mb: 2, alignItems: { md: 'center' } }}
          >
            <TextField
              size="small"
              placeholder="Buscar por nome ou RA"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              sx={{ minWidth: 280 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRounded />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <ToggleButtonGroup
              size="small"
              exclusive
              value={turma}
              onChange={(_, v) => v && setTurma(v)}
              sx={{ flexWrap: 'wrap' }}
            >
              <ToggleButton value="todas">Todas</ToggleButton>
              {r?.turmas.map((t) => (
                <ToggleButton key={t.codigo} value={t.codigo}>
                  {t.codigo}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Typography variant="body2" color="text.secondary">
              {filtrados.length} alunos
            </Typography>
          </Stack>

          <TableContainer component={Card}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <Cabecalho campo="nome" ordem={ordem} onOrdena={ordena}>
                    Aluno
                  </Cabecalho>
                  <Cabecalho campo="p1" ordem={ordem} onOrdena={ordena}>
                    P1 por disciplina
                  </Cabecalho>
                  <Cabecalho campo="vinculo" ordem={ordem} onOrdena={ordena}>
                    Vínculo
                  </Cabecalho>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtrados.map((a) => (
                  <TableRow key={a.ra} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {nomeProprio(a.nome)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        RA {a.ra} ·{' '}
                        {a.emails_cps.length
                          ? `${a.emails_cps.length} e-mail(s) @cps`
                          : 'sem e-mail @cps conhecido'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap' }}>
                        {a.matriculas.map((m) => (
                          <Tooltip
                            key={m.disciplina}
                            title={`T1 ${fmt(m.notas.T1)} · individual ${fmt(m.notas.IND1)} · grupo ${fmt(m.notas.GRU1)}${m.grupo ? ` (grupo ${m.grupo}${m.foraDoPrazo ? ', fora do prazo' : ''})` : ' · sem entrega'}${m.aws?.startsWith('e-mail') ? ' · AWS sem nome' : ''}`}
                          >
                            <Chip
                              size="small"
                              variant="outlined"
                              color={
                                typeof m.notas.P1 === 'number' && m.notas.P1 >= 6
                                  ? 'success'
                                  : 'default'
                              }
                              label={`${m.disciplina} ${fmt(m.notas.P1, 1)}`}
                            />
                          </Tooltip>
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {a.vinculado_em ? (
                        <Tooltip
                          title={`${a.conta_google} · ${a.email_cps} · último acesso ${dataBr(a.ultimo_acesso, true)}`}
                        >
                          <Chip
                            size="small"
                            color="success"
                            label={`Confirmado ${dataBr(a.vinculado_em)}`}
                          />
                        </Tooltip>
                      ) : a.pendente_desde ? (
                        <Chip
                          size="small"
                          color="warning"
                          variant="outlined"
                          label={`Link enviado ${dataBr(a.pendente_desde, true)}`}
                        />
                      ) : (
                        <Chip size="small" variant="outlined" label="Ainda não entrou" />
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Ver como o aluno vê">
                        <IconButton
                          component={RouterLink}
                          to={`/admin/aluno/${a.ra}?semestre=${semestre}`}
                          aria-label="Ver como o aluno vê"
                        >
                          <VisibilityRounded />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Aceitar outro e-mail @cps">
                        <IconButton
                          onClick={() => (setEmailPara(a), setNovoEmail(''))}
                          aria-label="Aceitar outro e-mail"
                        >
                          <AlternateEmailRounded />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Desfazer vínculo">
                        <span>
                          <IconButton
                            disabled={!a.vinculado_em}
                            onClick={() => setDesfazer(a)}
                            aria-label="Desfazer vínculo"
                          >
                            <LinkOffRounded />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Dialog open={Boolean(desfazer)} onClose={() => setDesfazer(null)}>
        <DialogTitle>Desfazer o vínculo?</DialogTitle>
        <DialogContent>
          <Typography>
            {desfazer && nomeProprio(desfazer.nome)} deixa de ver o painel com a conta{' '}
            {desfazer?.conta_google} e precisa confirmar o RA de novo.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDesfazer(null)}>Cancelar</Button>
          <Button
            color="error"
            variant="contained"
            onClick={async () => {
              await desfazVinculoAdmin(desfazer!.ra);
              setDesfazer(null);
              notify('Vínculo desfeito.');
              alunos.reload();
              resumo.reload();
            }}
          >
            Desfazer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(emailPara)} onClose={() => setEmailPara(null)} fullWidth maxWidth="xs">
        <DialogTitle>Aceitar outro e-mail @cps</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Já aceitos para {emailPara && nomeProprio(emailPara.nome)}:{' '}
              {emailPara?.emails_cps.join(', ') || 'nenhum (aceita qualquer @cps)'}
            </Typography>
            <TextField
              label="E-mail"
              value={novoEmail}
              onChange={(e) => setNovoEmail(e.target.value)}
              autoFocus
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailPara(null)}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={!novoEmail.includes('@')}
            onClick={async () => {
              await adicionaEmailAdmin(emailPara!.ra, novoEmail);
              setEmailPara(null);
              notify('E-mail aceito.');
              alunos.reload();
            }}
          >
            Aceitar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
